import { useIonToast } from '@ionic/react'
import { SmartChargingStatus } from '@vae/server/src/interfaces/eNode/eNodeSmartCharging.interface'
import { ENodeVehicle } from '@vae/server/src/interfaces/eNode/eNodeVehicle.interface'
import { useMemo, useState } from 'react'
import useUser from '../../../../contexts/hooks/useUser'
import { trpc } from '../../../../modules/trpc'

export type SmartChargeOverrideHook = ReturnType<typeof useSmartChargeOverride>

export const useSmartChargeOverride = (
	smartChargingStatus: SmartChargingStatus | null | undefined,
	vehicle: ENodeVehicle | null | undefined
) => {
	const utils = trpc.useContext()

	const { user } = useUser()

	const [actionIdInProcess, setActionIdInProcess] = useState<string | null>(null)
	const [isChargingOverride, setIsChargingOverride] = useState<boolean | null>(null)
	const [smartOverrideOverride, setSmartOverrideOverride] = useState<boolean | null>(null)
	const [presentToast] = useIonToast()

	const smartOverrideCharge = trpc.eNode.charging.smartOverride.create.useMutation({
		onSuccess: data => {
			if (!data) return

			setActionIdInProcess(data?.vendorActionId)
		},
		onError: error => {
			resetStateAndRefetch()

			presentToast({
				message: error.message,
				duration: 3000,
				color: 'danger',
				position: 'top',
			})
		},
	})

	const deleteSmartOverrideCharge = trpc.eNode.charging.smartOverride.delete.useMutation({
		onSuccess: data => {
			if (!data) return

			setActionIdInProcess(data?.vendorActionId)
		},
		onError: error => {
			resetStateAndRefetch()

			presentToast({
				message: error.message,
				duration: 3000,
				color: 'danger',
				position: 'top',
			})
		},
	})

	trpc.eNode.charging.getAction.useQuery(
		{ hashedPid: user!.hashedPid, actionId: actionIdInProcess ?? '' },
		{
			onSuccess(data) {
				if (data?.state !== 'PENDING') setTimeout(() => resetStateAndRefetch(), 10000) // 10 seconds
			},
			refetchInterval(data) {
				if (data?.state === 'PENDING') return 5000
				else return false
			},
			enabled: Boolean(actionIdInProcess),
			onError(error) {
				console.log(error)
			},
		}
	)

	const resetStateAndRefetch = () => {
		utils.eNode.vehicles.getSingle.invalidate()
		utils.eNode.vehicles.get.invalidate()
		utils.eNode.charging.getSmartChargingStatus.invalidate()

		setActionIdInProcess(null)
		setIsChargingOverride(null)
		setSmartOverrideOverride(null)
	}

	const isEnabled = useMemo(() => {
		if (smartOverrideOverride !== null) {
			return smartOverrideOverride
		}

		return Boolean(smartChargingStatus?.smartOverride?.createdAt)
	}, [smartOverrideOverride, smartChargingStatus?.smartOverride?.createdAt])

	const isCharging = useMemo(() => {
		if (isChargingOverride !== null) {
			return isChargingOverride
		}

		return vehicle?.chargeState.isCharging
	}, [isChargingOverride, vehicle?.chargeState.isCharging])
	if (!user || !vehicle) return null

	return {
		enable: () => {
			setIsChargingOverride(true)
			setSmartOverrideOverride(true)
			utils.eNode.charging.getSmartChargingStatus.invalidate()

			smartOverrideCharge.mutate({
				hashedPid: user.hashedPid!,
				vehicleId: vehicle.id,
			})
		},
		disable: () => {
			setIsChargingOverride(false)
			setSmartOverrideOverride(true)

			deleteSmartOverrideCharge.mutate({
				hashedPid: user.hashedPid!,
				vehicleId: vehicle.id,
			})
		},
		actionPending: Boolean(actionIdInProcess),
		isChargingOverride,
		smartOverrideOverride,
		isEnabled,
		isCharging,
	}
}
